import {useCallback, useMemo, useRef} from 'react';

import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import useIdentity from '@/hooks/useIdentity';

enum ValuePropsExperimentVariation {
  FALLBACK = 'fallback',
  CONTROL = 'control',
  TREATMENT = 'treatment',
}

const EXPERIMENT_VARIATIONS: string[] = Object.values(ValuePropsExperimentVariation);

const isExperimentVariation = (value: string): value is ValuePropsExperimentVariation =>
  EXPERIMENT_VARIATIONS.includes(value);

type ValuePropsExperiment = {
  isTreatment: boolean;
  trackExposed: () => void;
  experimentIsActive: boolean;
};

const useValuePropsExperiment = (): ValuePropsExperiment => {
  const {variation: rawVariation, trackExperimentExposed} = useStringAssignmentExperimentDetails(
    EppoFlags.ValueProps,
  );
  const variation = isExperimentVariation(rawVariation)
    ? rawVariation
    : ValuePropsExperimentVariation.FALLBACK;

  const {isLoggedIn, loading: isIdentityLoading} = useIdentity();
  const isEligible = !isIdentityLoading && !isLoggedIn;

  const isTreatment = isEligible && variation === ValuePropsExperimentVariation.TREATMENT;
  const experimentIsActive =
    variation === ValuePropsExperimentVariation.TREATMENT ||
    variation === ValuePropsExperimentVariation.CONTROL;

  const hasTrackedExposureRef = useRef(false);
  const trackExposed = useCallback(() => {
    if (
      isEligible &&
      variation !== null &&
      variation !== ValuePropsExperimentVariation.FALLBACK &&
      !hasTrackedExposureRef.current
    ) {
      trackExperimentExposed();
      hasTrackedExposureRef.current = true;
    }
  }, [isEligible, trackExperimentExposed, variation]);

  const value = useMemo(
    () => ({
      trackExposed,
      isTreatment,
      experimentIsActive,
    }),
    [trackExposed, isTreatment, experimentIsActive],
  );

  return value;
};

export default useValuePropsExperiment;
